import React, { useState, useCallback, useMemo, useContext } from 'react';
import { Button, Form, Input, Radio, Steps, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SyncOutlined } from '@ant-design/icons';
import { LorawanActionTypes } from '@utils/deviceActions';
import api from '@services/api';
import { hexadecimalGenerator } from '@utils/hexadecimalGenerator';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import cleanObject from '@utils/cleanObject';

import * as S from './styles';
import { LoraWanActivationTypes } from './ActivationButton';
import ConfigStep, {
  ConfigStepDataProps,
} from './ActivationModalSteps/ConfigStep';
import IdentifiersStep, {
  IdentifiersStepDataProps,
} from './ActivationModalSteps/IdentifiersStep';

const { Item } = Form;

export interface DeviceProps {
  activation?: 'OTAA' | 'ABP';
  encryption?: 'NS' | 'APP';
  devEui?: string;
  appEui?: string;
  description?: string;
  tags?: string[] | string;
  appKey?: string;
  devAddr?: string;
  nwksKey?: string;
  appsKey?: string;
  suspended?: boolean;
  deviceProfileId?: string;
  connections?: string[];
}

interface ActivationPayloadProps {
  action: LorawanActionTypes.ACTIVATION;
  contractId?: string;
  customerId?: number;
  accountId?: number;
  contractIdMOT?: string;
  devices: DeviceProps[];
}

interface ActivationModalProps {
  visible: boolean;
  activationType: LoraWanActivationTypes;
  onClose: () => void;
}

const ActivationModal = ({
  visible,
  activationType,
  onClose,
}: ActivationModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['lorawan', 'device-actions-modal']);
  const { selectedApp, isMot } = useContext(SessionContext);

  const [configForm] = Form.useForm();
  const [identifiersForm] = Form.useForm();

  const isBatch = ['batch', 'token'].includes(activationType);

  const [saveLoading, setSaveLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [uploadedDevices, setUploadedDevices] = useState<
    DeviceProps[] | undefined
  >();
  const [configValues, setConfigValues] = useState<ConfigStepDataProps>();

  const formByStep = useMemo(() => {
    const forms = [configForm, identifiersForm];
    return forms?.[step];
  }, [configForm, identifiersForm, step]);

  const postActivation = useCallback(
    (payload: any) => api.post('/lorawan/actions', payload),
    [],
  );

  const renderItemLabel = useCallback(
    (label: string, isRequired?: boolean) => (
      <>
        {isRequired && <span style={{ color: 'red' }}>*&nbsp;</span>}
        {t(label)}
      </>
    ),
    [t],
  );

  const renderLabelWithHexadecimalGenerate = useCallback(
    (label: string, numSize?: number) => (
      <>
        {renderItemLabel(label)}
        {numSize && (
          <Tooltip title={t('generateHexadecimal')}>
            <Button
              icon={<SyncOutlined />}
              type="link"
              size="small"
              onClick={() => {
                formByStep?.setFieldValue(label, hexadecimalGenerator(numSize));
              }}
            />
          </Tooltip>
        )}
      </>
    ),
    [formByStep, renderItemLabel, t],
  );

  const validateLength = useCallback(
    (value: string | undefined, size?: number) =>
      value && value.length === size
        ? Promise.resolve()
        : Promise.reject(new Error(t('fieldSize', { size }))),
    [t],
  );

  const renderInputItem = useCallback(
    (name: string, numSize?: number, validateNumSize?: boolean) => (
      <Item
        label={
          numSize
            ? renderLabelWithHexadecimalGenerate(name, numSize)
            : renderItemLabel(name)
        }
        name={name}
        rules={[
          {
            required: true,
            message: t('fieldIsRequired'),
          },
          ...(validateNumSize
            ? [
                {
                  validator: (_: any, value: string | undefined) =>
                    validateLength(value, numSize),
                },
              ]
            : []),
        ]}
      >
        <Input allowClear />
      </Item>
    ),
    [renderItemLabel, renderLabelWithHexadecimalGenerate, t, validateLength],
  );

  const renderRadioItem = useCallback(
    (name: string, options: string[], hideLabel?: boolean) => (
      <Item
        label={hideLabel ? undefined : t(name)}
        name={name}
        rules={[
          {
            required: true,
            message: t('fieldIsRequired'),
          },
        ]}
      >
        <Radio.Group defaultValue={options?.[0]} buttonStyle="solid">
          {options?.map(opt => (
            <Radio.Button key={opt} value={opt}>
              {opt}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Item>
    ),
    [t],
  );

  const payloadToSend = useCallback(
    (
      currentConfigValues?: ConfigStepDataProps,
      currentIdentifiersValues?: IdentifiersStepDataProps,
    ): ActivationPayloadProps => {
      const {
        requestId,
        contractMotId,
        contractLorawanId,
        contractId,
        ...configValuesRest
      } = currentConfigValues || {};

      const defaultPayload: ActivationPayloadProps = {
        action: LorawanActionTypes.ACTIVATION,
        contractId,
        devices: [],
      };

      if (isMot) {
        if (requestId) {
          // activation made from broker children
          defaultPayload.accountId = requestId;
        } else {
          // activation made from broker
          defaultPayload.contractIdMOT = contractMotId;
          defaultPayload.contractId = contractLorawanId;
        }
      }

      const deviceDefaultValues = {
        ...configValuesRest,
        ...currentIdentifiersValues,
      };

      if (configValuesRest.activation === 'OTAA') {
        deviceDefaultValues.encryption = 'NS';
      }

      let devices = [];

      if (isBatch) {
        delete deviceDefaultValues.uploadedDevices;
        devices =
          uploadedDevices?.map(device =>
            cleanObject({
              ...deviceDefaultValues,
              ...device,
              tags: `${device?.tags}`.split(';'),
              connections: `${device?.connections}`.split(';'),
            }),
          ) ?? [];
      } else {
        devices = currentConfigValues
          ? [
              {
                ...deviceDefaultValues,
                ...configValuesRest,
              },
            ]
          : [];
      }
      return {
        ...defaultPayload,
        // @ts-ignore
        devices,
      };
    },
    [isBatch, isMot, uploadedDevices],
  );

  const handleClose = useCallback(() => {
    identifiersForm.resetFields();
    configForm.resetFields();
    setStep(0);
    setUploadedDevices(undefined);
    setConfigValues(undefined);
    onClose();
  }, [configForm, identifiersForm, onClose]);

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}${
      isMot ? '/lorawan' : ''
    }/operational/actions-queue?actionType=0`;
    message.success(
      <>
        <span>{t('device-actions-modal:msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('device-actions-modal:msgs.follow-in-the-actions-queue')}
        </Button>
      </>,
      2,
    );
  }, [navigate, selectedApp, isMot, t]);

  const handleFormFinish = useCallback(
    async (payload: ActivationPayloadProps) => {
      setSaveLoading(true);
      try {
        await postActivation(payload);
        handleSuccess();
        handleClose();
      } catch {
        message.error(t('errorWhenActivating'));
      }
      setSaveLoading(false);
    },
    [handleClose, handleSuccess, postActivation, t],
  );

  const formValidate = useCallback(
    () =>
      formByStep?.validateFields().then(data => {
        formByStep.submit();
        return data;
      }),
    [formByStep],
  );

  const handleStep = useCallback(
    (action: 'next' | 'confirm') => {
      if (action === 'next') {
        formValidate().then(values => {
          setConfigValues(values);
          setStep(oldStep => oldStep + 1);
        });
      } else {
        formValidate().then(values => {
          if (isBatch) {
            setConfigValues(values);
            handleFormFinish(payloadToSend(values));
          } else {
            handleFormFinish(payloadToSend(configValues, values));
          }
        });
      }
    },
    [configValues, formValidate, handleFormFinish, isBatch, payloadToSend],
  );

  const modalSteps = useMemo(
    () => [
      {
        name: 'config',
        content: (
          <ConfigStep
            key={0}
            form={configForm}
            renderItemLabel={renderItemLabel}
            renderRadioItem={renderRadioItem}
            isBatch={isBatch}
            onUpload={setUploadedDevices}
          />
        ),
      },
      {
        name: 'identifiers',
        content: (
          <IdentifiersStep
            key={1}
            form={identifiersForm}
            renderItemLabel={renderItemLabel}
            renderInputItem={renderInputItem}
            activation={configValues?.activation}
            encryption={configValues?.encryption}
          />
        ),
      },
    ],
    [
      configForm,
      renderRadioItem,
      isBatch,
      identifiersForm,
      renderItemLabel,
      renderInputItem,
      configValues?.activation,
      configValues?.encryption,
    ],
  );

  const stepsTitles = useMemo(
    () => modalSteps?.map(({ name }) => ({ title: t(name) })),
    [modalSteps, t],
  );

  return (
    <S.ModalContainer
      bodyStyle={{ padding: '24px 40px' }}
      afterClose={() => document.body.style.removeProperty('overflow')}
      footer={
        <>
          {step > 0 && (
            <Button onClick={() => setStep(oldStep => oldStep - 1)}>
              {t('buttons.back')}
            </Button>
          )}

          {isBatch || step === 1 ? (
            <Button
              type="primary"
              onClick={() => handleStep('confirm')}
              loading={saveLoading}
            >
              {t('buttons.confirm')}
            </Button>
          ) : (
            <Button type="primary" onClick={() => handleStep('next')}>
              {t('buttons.next')}
            </Button>
          )}
        </>
      }
      maskClosable={false}
      title={t(`${activationType}Activation`)}
      open={visible}
      centered
      forceRender
      onCancel={() => {
        document.body.style.removeProperty('overflow');
        handleClose();
      }}
    >
      {!isBatch && (
        <Steps
          size="small"
          current={step}
          items={stepsTitles}
          style={{ marginBottom: 48 }}
        />
      )}

      {modalSteps?.[step]?.content}
    </S.ModalContainer>
  );
};

export default ActivationModal;
